'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertCircle, Info, X } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  createdAt: number;
}

interface ToastContextValue {
  toast: (type: ToastType, title: string, message: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const AUTO_DISMISS_MS = 5000;
const MAX_TOASTS = 3;

const ICON_MAP: Record<ToastType, typeof Check> = {
  success: Check,
  error: AlertCircle,
  info: Info,
};

const COLOR_MAP: Record<ToastType, string> = {
  success: 'var(--success, #22C55E)',
  error: 'var(--danger, #DC2626)',
  info: 'var(--info, #64748B)',
};

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Single toast                                                       */
/* ------------------------------------------------------------------ */

function ToastCard({
  toast: t,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const Icon = ICON_MAP[t.type];
  const color = COLOR_MAP[t.type];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 80 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 80 }}
      transition={{ type: 'spring', damping: 22, stiffness: 300 }}
      className="pointer-events-auto relative flex w-full max-w-[360px] items-start gap-3 overflow-hidden rounded-xl border p-4 shadow-lg"
      style={{
        background: 'var(--surface, #FFFFFF)',
        borderColor: 'var(--border, #EBE8E3)',
      }}
    >
      {/* Icon circle */}
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ background: color }}
      >
        <Icon className="h-3.5 w-3.5 text-white" />
      </div>

      {/* Text */}
      <div className="min-w-0 flex-1">
        <p
          className="text-sm font-semibold leading-tight"
          style={{ color: 'var(--text, #1C1917)' }}
        >
          {t.title}
        </p>
        <p
          className="mt-0.5 text-[13px] leading-snug"
          style={{ color: 'var(--text-secondary, #57534E)' }}
        >
          {t.message}
        </p>
      </div>

      {/* Close */}
      <button
        onClick={() => onDismiss(t.id)}
        className="shrink-0 rounded p-0.5 transition-colors hover:bg-black/5"
        aria-label="Dismiss"
      >
        <X
          className="h-4 w-4"
          style={{ color: 'var(--text-muted, #9CA3AF)' }}
        />
      </button>

      {/* Progress bar */}
      <motion.div
        className="absolute bottom-0 left-0 h-1"
        style={{ background: color }}
        initial={{ width: '100%' }}
        animate={{ width: '0%' }}
        transition={{ duration: AUTO_DISMISS_MS / 1000, ease: 'linear' }}
      />
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Mount-gated portal. `typeof window !== 'undefined'` differs between
  // server (false) and client first render (true), so React saw an HTML
  // element on the client where the server rendered nothing — triggering
  // React error #418 hydration mismatch on EVERY dashboard route
  // (ToastProvider wraps the entire dashboard tree via layout.tsx).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (type: ToastType, title: string, message: string) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setToasts((prev) => [{ id, type, title, message, createdAt: Date.now() }, ...prev].slice(0, MAX_TOASTS));
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {mounted &&
        createPortal(
          <div role="status" aria-live="polite" className="pointer-events-none fixed top-6 right-6 z-[9999] flex flex-col gap-2">
            <AnimatePresence mode="popLayout">
              {toasts.map((t) => (
                <ToastCard key={t.id} toast={t} onDismiss={dismiss} />
              ))}
            </AnimatePresence>
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}
